import React from 'react';
import { addHours, addMonths, isAfter, isBefore, startOfDay, subMonths, differenceInMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import slugify from 'slugify';
import parse from 'html-react-parser';
// import styles from './styles/ManageContractsTableRow.module.scss';

// Types.
import { ManageContractsTableRowProps } from '../../../types/contract';
// Utils.
import { formatDate, setPriceFormat } from '../../../utils/billing';
import { ManageContractsApp } from './contractManagementContext';

export const ManageContractsTableRow: React.FunctionComponent<ManageContractsTableRowProps> = (props: ManageContractsTableRowProps) => {
  const { t } = useTranslation();
  const dispatch = ManageContractsApp.useDispatch();
  const [open, setOpen] = React.useState<boolean>(false);
  const {
    newContract,
    contractNo,
    selected,
    currentContract,
    contractEndDate,
    contractTitle,
    contractName,
    basePrice,
    energyDeliveries,
    energyPrice,
    tableHasSpot,
    contractIsSpot,
    margin,
    contractLength,
    contractDescription,
    contractOffer,
    offerDuration,
    offerPrice,
    contractNumber,
    // contractExtraServices,
    contractHasPaperBilling,
    contractHasVaihtoturva,
    useVaihtoturva,
  } = props;

  const { filteredContracts, selectedForm4, startDate } = ManageContractsApp.useState();

  const id = slugify(contractTitle + Math.random() * 100).toLowerCase();

  const name = newContract ? 'newContract' : 'oldContract';

  const isTemporary =
    contractEndDate !== '2073-06-01T00:00:00' &&
    ((typeof contractLength === 'number' && contractLength > 0) ||
      (typeof contractEndDate === 'string' && contractEndDate.length > 0 && isBefore(new Date(contractEndDate), new Date('2073-06-01T00:00:00'))));
  const isEditable =
    selectedForm4 === 2 || selectedForm4 === 3
      ? false
      : !newContract && (contractHasVaihtoturva || (isTemporary && differenceInMonths(new Date(contractEndDate || new Date()), new Date()) >= 6));

  const hideEnergy = contractTitle.toLowerCase().includes('sähköpaketti');

  return (
    <div className={`manage-contracts-table-row ${open ? 'open' : ''} ${selected ? 'selected' : ''} ${contractLength ? '' : 'ongoing'}`}>
      {newContract && currentContract && contractEndDate && isBefore(new Date(contractEndDate), new Date('2073-06-01T00:00:00')) && (
        <p>{t('manageContracts:manageContracts.TableContinueWithCurrent', { endDate: formatDate(contractEndDate) })}</p>
      )}
      {newContract && currentContract && (!contractEndDate || (contractEndDate && contractEndDate === '2073-06-01T00:00:00')) && (
        <p>{t('manageContracts:manageContracts.TableContinueWithCurrentForNow')}</p>
      )}
      <div className="table-cell form-item radio">
        <input
          type="radio"
          name={`${name}-contract-type`}
          id={id}
          onChange={(): void => {
            if (!newContract) {
              dispatch(['changeOldContract', filteredContracts.find((c) => c.contractCode === contractNo)]);
            }
            if (newContract && contractNo) {
              dispatch(['changeNewContract', contractNo]);
            }
            // setSelected(contractNo?.toString());

            if (!newContract && contractEndDate && isTemporary && selectedForm4 !== 3) {
              dispatch(['changeEnd', addHours(startOfDay(new Date(contractEndDate)), 12)]);
              dispatch(['changeStart', addHours(startOfDay(new Date(contractEndDate)), 12)]);
            }
            if (contractLength === 6 && isAfter(startDate || new Date(), addMonths(startOfDay(new Date()), 3))) {
              dispatch(['changeStart', addMonths(startOfDay(new Date()), 3)]);
            }

            const tariff = contractNo?.split('-')[1];

            if (tariff && (parseInt(tariff) === 214 || parseInt(tariff) === 4) && isAfter(startDate || new Date(), addMonths(new Date(), 2))) {
              dispatch(['changeStart', addMonths(new Date(), 2)]);
            }
          }}
          checked={useVaihtoturva === contractNo?.toString() ? true : selected}
          disabled={useVaihtoturva === contractNo?.toString() ? false : selectedForm4 === 2 && contractHasPaperBilling ? false : isEditable}
        />
        <div className="form-item-input"></div>
        <span className="mobile-label">{t('manageContracts:manageContracts.TableContract')}</span>
        <label htmlFor={id}>
          {contractTitle}
          {/* To be enabled in LE-262
          {contractExtraServices && contractExtraServices.length > 0 && contractExtraServices.map(service => 
            <div key={service.externalName} className={styles.extraService}><strong>{t('manageContracts:manageContracts.TableExtraService')}:</strong> {service.externalName}</div>
          )}
          */}
        </label>
      </div>
      {newContract && (
        <div className="table-cell">
          <span className="mobile-label">{t('manageContracts:manageContracts.TableBase')}</span>
          {basePrice
            ? `${basePrice.toFixed(2).replace('.', ',')} ${t('manageContracts:manageContracts.TableBaseUnit')}`
            : `0 ${t('manageContracts:manageContracts.TableBaseUnit')}`}
        </div>
      )}
      {newContract && (
        <div className="table-cell">
          <span className="mobile-label">{t('manageContracts:manageContracts.TableEnergy')}</span>
          {energyDeliveries &&
            energyDeliveries?.length > 1 &&
            !energyDeliveries.find((d) => d.elementNo === 10) &&
            energyDeliveries.map((d) => (
              <>
                {d.priceInclVat?.toFixed(2).replace('.', ',')}{' '}
                {d.elementName.toLowerCase().replace('energia', '').replace('talviarki', 'talvi').replace('muuaika', 'muu')} <br />
              </>
            ))}
          {energyDeliveries && energyDeliveries?.length > 1 && energyDeliveries.find((d) => d.elementNo === 10) && (
            <>
              {t(`manageContracts:manageContracts.TableSpotPriceFixings`)} {energyDeliveries.length}{' '}
              {t(`manageContracts:manageContracts.TableSpotPriceFixingsUnit`)}
            </>
          )}

          {(!energyDeliveries || energyDeliveries?.length < 2) && (
            <>
              {tableHasSpot && contractIsSpot
                ? 'Spot'
                : energyPrice && energyPrice > 0 && !hideEnergy
                  ? `${energyPrice?.toFixed(2).replace('.', ',')} ${t('manageContracts:manageContracts.TableEnergyUnit')}`
                  : '-'}
            </>
          )}
        </div>
      )}
      {newContract && tableHasSpot && (
        <div className="table-cell margin">
          <span className="mobile-label">{t('manageContracts:manageContracts.TableMargin')}</span>
          {contractIsSpot ? `${margin?.toFixed(2).replace('.', ',')} ${t('manageContracts:manageContracts.TableMarginUnit')}` : '-'}
        </div>
      )}
      {newContract && (
        <div className="table-cell">
          <span className="mobile-label">{t('manageContracts:manageContracts.TablePeriod')}</span>
          {contractLength && isTemporary
            ? `${contractLength} ${t('manageContracts:manageContracts.TablePeriodUnit')}`
            : t('contracts:contracts:ongoing')}
        </div>
      )}

      {!newContract && contractNumber && (
        <div className="table-cell">
          <span className="mobile-label">{t('common:general.contractNumber')}</span>
          {contractNumber}
        </div>
      )}
      {!newContract && (
        <div className="table-cell">
          <span className="mobile-label">{t('billing:billing.sortContractType')}</span>
          {isTemporary ? t('contracts:contracts.temporary') : t('contracts:contracts.ongoing')}
        </div>
      )}
      {!newContract && (
        <div className="table-cell">
          <span className="mobile-label">{t('manageContracts:manageContracts.TableEnds')}</span>
          {isTemporary && contractEndDate ? formatDate(contractEndDate) : '-'}
        </div>
      )}
      {contractDescription && (
        <button
          className="inline-link arrow"
          onClick={(e): void => {
            e.preventDefault();
            setOpen(!open);
          }}
        >
          {t(open ? 'common:general:close' : 'common:general:readMore')}
        </button>
      )}
      {newContract && !contractDescription && <div className="table-cell" />}
      {contractDescription && open && <div className="more">{parse(contractDescription)}</div>}
      {newContract && contractOffer && offerDuration !== undefined && offerDuration > 0 && (
        <div className="campaign-offer">
          <strong>{t('manageContracts:manageContracts.TableCampaignOffer')}</strong>
          {t('manageContracts:manageContracts.TableCampaignOfferBody', {
            duration: offerDuration ?? 0,
            price: setPriceFormat((offerPrice ?? 0).toString()),
          })}
        </div>
      )}
      {selectedForm4 !== 2 && !newContract && isEditable && contractEndDate && (
        <div className="campaign-offer non-editable">
          {t('manageContracts:manageContracts.OldAddressContractIsTemporary', { contractName: contractName, endDate: formatDate(contractEndDate) })}
          {contractHasVaihtoturva
            ? t('manageContracts:manageContracts.OldAddressContractHasVaihtoturva')
            : t('manageContracts:manageContracts.OldAddressContractIsTemporaryEditable', {
                editDate: formatDate(subMonths(new Date(contractEndDate), 6).toISOString()),
              })}
        </div>
      )}
      {!newContract && isEditable && contractEndDate && contractHasVaihtoturva && (
        <div className="form-item checkbox vaihtoturva">
          <input
            type="checkbox"
            id={`${id}-vaihtoturva`}
            onChange={(): void => {
              if (useVaihtoturva === contractNo?.toString()) {
                dispatch(['changeUseVaihtoturva', undefined]);
                dispatch(['changeOldContract', undefined]);
              } else {
                dispatch(['changeUseVaihtoturva', contractNo?.toString()]);
                dispatch(['changeOldContract', filteredContracts.find((c) => c.contractCode === contractNo?.toString())]);
              }
            }}
            checked={useVaihtoturva === contractNo?.toString()}
          />
          <div className="form-item-input"></div>
          <label htmlFor={`${id}-vaihtoturva`}>{t('manageContracts:manageContracts.useVaihtoturva')}</label>
        </div>
      )}
    </div>
  );
};

export default ManageContractsTableRow;
